import { rootActions, shippingAddressActions, useAppDispatch, useAppSelector } from '../../../data/store';
import { getShippingOptions, updateShippingCode } from '../../../data/data-service';
import { getLocalData } from '../../../domain/shopping-cart-address-provider';
import { ChangeEvent, FocusEvent, useState } from 'react';
import Preloader from '../../../../../../ui/svg/common/preloader';
import { IShippingResponse } from '../../../interfaces';
import SelectSimpleField from '../../../../../../ui/forms/fields/select-simple-field';
import { updateControl, validateControl } from '../../../../../../domain/validation/validation';
import { getFirstServerError } from '../../../../../../domain/validation/server-errors/server-error-provider';

const ShippingOptions = () => {

    const [loading, setLoading] = useState(false);

    const dispatch = useAppDispatch();

    const isShippingDifferentFromBilling = useAppSelector(store => store.shippingAddress.isShippingDifferentFromBilling);

    // this country / state data is used for tax calculations
    const countries = useAppSelector(store => store.root.countries);

    const billingAddressLine1Control = useAppSelector(store => store.billingAddress.addressLine1Control);
    const shippingAddressLine1Control = useAppSelector(store => store.shippingAddress.addressLine1Control);

    const billingCityControl = useAppSelector(store => store.billingAddress.cityControl);
    const shippingCityControl = useAppSelector(store => store.shippingAddress.cityControl);

    const billingCountryIdControl = useAppSelector(store => store.billingAddress.countryIdControl);
    const shippingCountryIdControl = useAppSelector(store => store.shippingAddress.countryIdControl);

    const billingStateIdControl = useAppSelector(store => store.billingAddress.stateIdControl);
    const shippingStateIdControl = useAppSelector(store => store.shippingAddress.stateIdControl);

    const billingSelectedCountry = countries.find(country => country.countryID.toString() === billingCountryIdControl.value.toString());
    const shippingSelectedCountry = countries.find(country => country.countryID.toString() === shippingCountryIdControl.value.toString());

    const billingSelectedState = billingSelectedCountry && billingSelectedCountry.states.find(state => state.stateID.toString() === billingStateIdControl.value.toString());
    const shippingSelectedState = shippingSelectedCountry && shippingSelectedCountry.states.find(state => state.stateID.toString() === shippingStateIdControl.value.toString());

    const upsShippingCodeControl = useAppSelector(store => store.shippingAddress.upsShippingCodeControl);
    const upsShippingOptions = useAppSelector(store => store.shippingAddress.upsShippingOptions);
    const upsShippingError = useAppSelector(store => store.shippingAddress.upsShippingError);

    const hasShippingDropdown = upsShippingOptions && upsShippingOptions.length > 0;

    const getValidationError = () => {
        let errorMessage = '';

        // validate billing fields
        if(!isShippingDifferentFromBilling){

            if(!billingAddressLine1Control.value){
                return 'This address field is required.';
            }

            if(!billingCityControl.value){
                return 'This city field is required.';
            }

            if(!billingSelectedCountry){
                return 'Please select a country.';
            }

            if(billingSelectedCountry.states && billingSelectedCountry.states.length > 0 && !billingSelectedState){
                return 'Please select a state.';
            }
        }
        else{
            if(!shippingAddressLine1Control.value){
                return 'This address field is required.';
            }

            if(!shippingCityControl.value){
                return 'This city field is required.';
            }

            // validate shipping fields
            if(!shippingSelectedCountry){
                return 'Please select a country.';
            }

            if(shippingSelectedCountry.states && shippingSelectedCountry.states.length > 0 && !shippingSelectedState){
                return 'Please select a state.';
            }
        }

        return errorMessage;
    };

    /**
     * load UPS shipping options + osco from the server
     */
    const openShippingOptions = async () => {
        dispatch(
            shippingAddressActions.main({
                upsShippingError: '',
            })
        );

        const errorMessage = getValidationError();
        if(errorMessage){
            dispatch(
                shippingAddressActions.main({
                    upsShippingError: errorMessage,
                })
            );
            return;
        }

        setLoading(true);

        const localData = getLocalData();
        const response = await getShippingOptions(localData);

        setLoading(false);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData){

            dispatch(
                shippingAddressActions.main({
                    upsShippingError: getFirstServerError(response),
                })
            );
            return;
        }

        const data = response.data.relatedData as IShippingResponse;

        dispatch(
            shippingAddressActions.main({
                upsShippingOptions: data.rates || [],
            })
        )
    };

    const onShippingCodeChange = async (evt: ChangeEvent) => {

        const $target = evt.target as HTMLInputElement;
        const shippingCode = $target.value;

        const localData = getLocalData();
        localData.shippingCode = shippingCode;

        setLoading(true);
        const response = await updateShippingCode(localData);
        setLoading(false);

        dispatch(
            shippingAddressActions.setControl({
                controlName: 'upsShippingCodeControl',
                control: updateControl(upsShippingCodeControl, shippingCode, !upsShippingCodeControl.isValid)
            })
        );

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData) return;

        dispatch(
            rootActions.setCartData(response.data.relatedData)
        );
    };

    /**
     * validate on blur
     */
    const onShippingCodeBlur = (evt: FocusEvent) => {
        dispatch(
            shippingAddressActions.setControl({
                controlName: 'upsShippingCodeControl',
                control: validateControl(upsShippingCodeControl),
            })
        );
    };

    return (
        <>
            {
                loading &&
                <div className="mt-4">
                    <Preloader width="20px" height="20px" color="#4f4f4f" />
                    <span className="ml-3">Getting shipping options...</span>
                </div>
            }

            {
                !loading &&
                <div id="ups-shipping-code">
                    {
                        !hasShippingDropdown &&
                        <div>
                            <button
                                type="button"
                                className="text-tiny text-blue-200 font-bold mt-4"
                                onClick={ openShippingOptions }>+ Click to select Shipping Options</button>
                        </div>
                    }

                    {
                        hasShippingDropdown &&
                        <div className="my-4" data-qa="cart-step3-ship-drop">
                            <SelectSimpleField
                                id="ups-shipping-code-select"
                                label="Select Shipping Option"
                                initialSelectionText="Select Shipping Option"
                                control={ upsShippingCodeControl }
                                onChange={ onShippingCodeChange }
                                onBlur={ onShippingCodeBlur }
                                options={ upsShippingOptions ? upsShippingOptions.map(shippingOption => {
                                    return {
                                        key: shippingOption.code,
                                        value: shippingOption.code,
                                        text: shippingOption.dropdownItemText,
                                    }
                                }) : [] }
                            />
                        </div>
                    }

                    {
                        upsShippingError &&
                        <div className="error-box my-2 text-base"><p>{ upsShippingError }</p></div>
                    }
                </div>
            }

            <div className="border rounded bg-gray-100 flex px-4 py-3 my-3">
                We will process your order within 1-3 business days.<br/>
                Shipping does not include duties and taxes. You will be responsible for those
                upon delivery of the product.
            </div>
        </>
    )
};

export default ShippingOptions;